import { ElMessage, ElMessageBox } from 'element-plus'
import { ref } from 'vue'
import { uploadFile, download } from '@/api/File'
import { isFireFax } from './is'
import dayjs from 'dayjs'
import { SelectOption } from '@/components/Form/src/types'
/**
 *
 * @param component 需要注册的组件
 * @param alias 组件别名
 * @returns any
 */
export const withInstall = <T>(component: T, alias?: string) => {
  const comp = component as any
  comp.install = (app: any) => {
    app.component(comp.name || comp.displayName, component)
    if (alias) {
      app.config.globalProperties[alias] = component
    }
  }
  return component as T & Plugin
}

/**
 * @param str 需要转下划线的驼峰字符串
 * @returns 字符串下划线
 */
export const humpToUnderline = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

/**
 * @param str 需要转驼峰的下划线字符串
 * @returns 字符串驼峰
 */
export const underlineToHump = (str: string): string => {
  if (!str) return ''
  return str.replace(/\-(\w)/g, (_, letter: string) => {
    return letter.toUpperCase()
  })
}

/**
 * 驼峰转横杠
 */
export const humpToDash = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

export const setCssVar = (prop: string, val: any, dom = document.documentElement) => {
  dom.style.setProperty(prop, val)
}

export const getCssVar = (prop: string, dom = document.documentElement) => {
  return getComputedStyle(dom).getPropertyValue(prop)
}

/**
 * 查找数组对象的某个下标
 * @param {Array} ary 查找的数组
 * @param {Functon} fn 判断的方法
 */
// eslint-disable-next-line
export const findIndex = <T = Recordable>(ary: Array<T>, fn: Fn): number => {
  if (ary.findIndex) {
    return ary.findIndex(fn)
  }
  let index = -1
  ary.some((item: T, i: number, ary: Array<T>) => {
    const ret: T = fn(item, i, ary)
    if (ret) {
      index = i
      return ret
    }
  })
  return index
}

export const trim = (str: string) => {
  return str.replace(/(^\s*)|(\s*$)/g, '')
}

/**
 * @param {Date | number | string} time 需要转换的时间
 * @param {String} fmt 需要转换的格式 如 yyyy-MM-dd、yyyy-MM-dd HH:mm:ss
 */
export function formatTime(time: Date | number | string, fmt: string) {
  if (!time) return ''
  else {
    const date = new Date(time)
    const o = {
      'M+': date.getMonth() + 1,
      'd+': date.getDate(),
      'H+': date.getHours(),
      'm+': date.getMinutes(),
      's+': date.getSeconds(),
      'q+': Math.floor((date.getMonth() + 3) / 3),
      S: date.getMilliseconds()
    }
    if (/(y+)/.test(fmt)) {
      fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length))
    }
    for (const k in o) {
      if (new RegExp('(' + k + ')').test(fmt)) {
        fmt = fmt.replace(
          RegExp.$1,
          RegExp.$1.length === 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length)
        )
      }
    }
    return fmt
  }
}

/**
 * 生成随机字符串
 */
export function toAnyString() {
  const str: string = 'xxxxx-xxxxx-4xxxx-yxxxx-xxxxx'.replace(/[xy]/g, (c: string) => {
    const r: number = (Math.random() * 16) | 0
    const v: number = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString()
  })
  return str
}

/**
 * 首字母大写
 */
export function firstUpperCase(str: string) {
  return str.toLowerCase().replace(/( |^)[a-z]/g, (L) => L.toUpperCase())
}

/**
 * 把对象转为formData
 */
export function objToFormData(obj: Recordable) {
  const formData = new FormData()
  Object.keys(obj).forEach((key) => {
    formData.append(key, obj[key])
  })
  return formData
}

export function filterStringTrim(data) {
  if (typeof data == 'object' && data) {
    Object.keys(data).forEach((key) => {
      if (typeof data[key] == 'object') {
        filterStringTrim(data[key])
      }

      if (typeof data[key] == 'string') {
        data[key] = data[key].trim()
      }
    })
  }
  return data
}

/**
 *
 *@description 校验表单提交是否有必填字段为空，为空触发滚动，否则，进入回调
 *@param elFormRef 表单数据源
 *@param callback 验证通过后，回调函数
 *@param loading 加载效果
 *@param errorClassName 错误类名
 */
export const validateForm = async (
  elFormRef: any,
  callback: Function,
  loading = ref(false),
  errorClassName = 'el-form-item__error'
) => {
  await elFormRef!.validate(async (valid: boolean) => {
    loading.value = true
    try {
      if (valid) {
        await callback()
      } else {
        setTimeout(() => scrollByclassName(errorClassName, 'end'), 100)
      }
    } finally {
      loading.value = false
    }
  })
}

/* 
    循环数组给 options 加数据 
  * Arr  数组 data
  * optionName  options 名
  * val   对应 value
  * label 对应 label
*/
export const filterOptions = (arr: Array<any>, optionName, val, label) => {
  arr.forEach((item) => {
    optionName.push({
      value: item[val],
      label: item[label]
    })
  })
}

/**
 *
 *@description 当捕抓到dom树上有这个类，页面就会自动滚动到类数组的第一个所在的位置。建议使用场景：表单空校验
 *@param className 错误类的类名
 */
export const scrollByclassName = (className, scrollMethod: any = 'end') => {
  if (!className) return
  document.getElementsByClassName(className)[0].scrollIntoView({
    block: scrollMethod,
    behavior: 'smooth'
  })
}

export const sendMsg = (success: Boolean, msg: string, time = 8000) => {
  success
    ? ElMessage({
        type: 'success',
        message: msg,
        duration: time,
        showClose: true
      })
    : ElMessage({
        type: 'error',
        message: msg,
        duration: time,
        showClose: true
      })
}

export const sendConfirmMsg = (
  message: string,
  title: string,
  okCallback: Function,
  cancelCallback: Function
) => {
  ElMessageBox.confirm(message, title, {
    confirmButtonText: 'OK',
    cancelButtonText: 'Cancel',
    type: 'warning'
  })
    .then(async () => {
      await okCallback()
    })
    .catch(async () => {
      await cancelCallback()
    })
}

/**
 * @file 拿到文件 path
 * @fileName 拿到文件 name
 */

export const previewImg = (file, fileName) => {
  download(file).then((res: any) => {
    if ('download' in document.createElement('a') && res.data) {
      const type = res.data.type as string
      const isOpenFile = type.includes('image') || type.includes('text') || type.includes('pdf')
      const blob = new Blob([res.data], { type }) //处理文档流
      const elink = document.createElement('a')
      if (isOpenFile && !isFireFax()) {
        elink.target = '_blank'
      } else {
        elink.download = file.name
      }
      elink.style.display = 'none'
      elink.download = fileName
      elink.href = URL.createObjectURL(blob)
      document.body.appendChild(elink)
      elink.click()
      URL.revokeObjectURL(elink.href) // 释放URL 对象
      document.body.removeChild(elink)
    } else {
      // error handler
    }
  })
}

// 文件上传
export const uploadFn = async (obj, folderName, fileName = '') => {
  if (obj.raw) {
    const formdata = { file: obj.raw, folderName, fileName }

    const res: any = await uploadFile(formdata)
    if (res.success) {
      return res
    } else {
      sendMsg(res.success, res.message)
      return false
    }
  }
}

/**
 * @arr select multiple option
 * @optionList select option list
 * @currentValue select option value
 * @currentLabel select option label
 */

export const filterMultipleTxt = (
  arr,
  optionList,
  currentValue = 'value',
  currentLabel = 'label'
) => {
  let txtDesc = ''

  for (const item of arr) {
    for (const sItem of optionList) {
      if (item == sItem[currentValue]) {
        txtDesc += sItem[currentLabel] + ','
      }
    }
  }

  return txtDesc.slice(0, -1)
}

// 拿到当前路由的一级路由名字
export const interceptRouterName = (path) => {
  if (path) {
    const splitChar = path.split('/')

    // console.log(splitChar, 'spc')
    if (splitChar[1] == 'view-verify-trainee') {
      return splitChar[1]
    } else {
      return splitChar[1] + splitChar[2]
    }
  }
}

export const countYear = (subYear, addYear) => {
  const arr: SelectOption = []

  for (let i = 0; i < subYear; i++) {
    const year = dayjs().subtract(i, 'year').format('YYYY')

    arr.unshift({
      label: year,
      value: Number(year)
    })
  }

  for (let i = 1; i <= addYear; i++) {
    const year = dayjs().add(i, 'year').format('YYYY')

    arr.push({
      label: year,
      value: Number(year)
    })
  }

  return arr
}

export const filterFileName = (fileName) => {
  const txt = fileName.lastIndexOf('/')

  return fileName.slice(txt + 1)
}

/**
 * 官方动态加载图片方法
 */
export function getAssetsFile(url) {
  return new URL(`../assets/imgs/${url}`, import.meta.url).href
}

/*
 * api 请求接口
 * name 文件名字
 * format 文件格式
 */

export const downloadExcel = async (api, name, format = 'application/vnd.ms-excel') => {
  const res = await api

  if (res.data.type != 'application/json') {
    const link = document.createElement('a')
    const blob = new Blob([res.data], { type: format })
    link.style.display = 'none'
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', name)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } else {
    sendMsg(false, 'Download error')
  }
}

//  格式化金钱 example: 3,000
export function formatPrice(price) {
  return String(price).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}
